import { Capacitor } from "@capacitor/core";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NotificationItem } from "../../components/notificationItem/NotificationItem";
import { useAuth } from "../../contexts/AuthProvider";
import { useNotificationsContext } from "../../contexts/NotificationsContext";
import { pushNotificationService } from "../../services/pushNotification.service";
import { trackErrorEvent, trackEvent } from "../../utils/analytics";
import type { NotificationResponse } from "../../utils/models";

const NotificationSkeleton = () => (
	<Paper
		elevation={0}
		sx={{
			width: "100%",
			p: 2,
			borderRadius: 2,
		}}
	>
		<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
			<Box sx={{ flex: 1 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
					<Skeleton variant="text" width={120} height={24} />
					<Skeleton variant="circular" width={24} height={24} />
				</Box>
				<Skeleton variant="text" width="40%" height={20} />
				<Box sx={{ mt: 1, display: "flex", gap: 1 }}>
					<Skeleton variant="rounded" width={100} height={24} />
					<Skeleton variant="rounded" width={100} height={24} />
				</Box>
			</Box>
			<Skeleton variant="rounded" width={80} height={24} />
		</Box>
	</Paper>
);

export const NotificationsPage: FC = () => {
	const { t } = useTranslation();
	const { allNotifications, markNotificationsSeen, markNotificationClicked, isLoading } = useNotificationsContext();
	const { userInfo } = useAuth();
	const [notifications, setNotifications] = useState<NotificationResponse[]>([]);

	// Helper function to track events with common parameters
	const trackNotificationEvent = (eventName: string, additionalData: Record<string, any> = {}) => {
		if (!userInfo?.id) return;

		trackEvent(eventName, {
			user_id: userInfo.id,
			timestamp: new Date().toISOString(),
			session_id: sessionStorage.getItem("session_id") || "unknown",
			...additionalData,
		});
	};

	// Track page view when component mounts
	useEffect(() => {
		let isMounted = true;

		const trackPageView = () => {
			if (!userInfo || !isMounted) return;

			// Track page view with timing data and notification metrics
			const notificationTypeCounts: Record<string, number> = {};
			allNotifications?.forEach((notification) => {
				const type = notification.notification_type;
				notificationTypeCounts[type] = (notificationTypeCounts[type] || 0) + 1;
			});

			trackNotificationEvent("notifications_page_viewed", {
				total_notifications: allNotifications?.length || 0,
				unseen_notifications: allNotifications?.filter((n) => !n.isSeen).length || 0,
				clicked_notifications: allNotifications?.filter((n) => n.isClicked).length || 0,
				notification_types_distribution: notificationTypeCounts,
				referrer: document.referrer || "direct",
			});
		};

		trackPageView();

		return () => {
			isMounted = false;
		};
	}, [userInfo?.id, allNotifications]); // Added allNotifications as dependency for notification counts

	// Process and sort notifications with useMemo
	const sortedNotifications = useMemo(() => {
		if (!allNotifications) return [];

		try {
			if (allNotifications.length === 0) {
				return [];
			}

			// Sort by creation date (newest first)
			return Array.from(allNotifications).sort((a, b) => b.created_at.valueOf() - a.created_at.valueOf());
		} catch (error) {
			console.error("Error processing notifications:", error);
			return [];
		}
	}, [allNotifications]);

	// Set notifications state and track analytics
	useEffect(() => {
		setNotifications(sortedNotifications);

		if (!userInfo?.id || sortedNotifications.length === 0) return;

		// Count notifications by type
		const typeDistribution: Record<string, number> = {};
		sortedNotifications.forEach((notification) => {
			const type = notification.notification_type;
			typeDistribution[type] = (typeDistribution[type] || 0) + 1;
		});
	}, [sortedNotifications, userInfo?.id]);

	// Mark notifications as seen in a separate useEffect
	useEffect(() => {
		let isMounted = true;

		const markAllAsSeen = async () => {
			if (!allNotifications || allNotifications.length === 0 || !userInfo?.id) return;

			// Get IDs of only unseen notifications
			const unseenNotificationIds = allNotifications.filter((notification) => !notification.isSeen).map((notification) => notification.id);

			if (unseenNotificationIds.length === 0) return;

			try {
				// Mark unseen notifications as seen
				const success = await markNotificationsSeen(unseenNotificationIds);

				// If component unmounted during async operation, don't proceed
				if (!isMounted) return;

				if (success && userInfo?.id) {
					// Count notifications by type
					const typeCountMap: Record<string, number> = {};
					unseenNotificationIds.forEach((id) => {
						const notification = allNotifications.find((n) => n.id === id);
						if (notification) {
							const type = notification.notification_type;
							typeCountMap[type] = (typeCountMap[type] || 0) + 1;
						}
					});

					trackNotificationEvent("notifications_marked_seen_on_page_load", {
						count: unseenNotificationIds.length,
						notification_types_distribution: typeCountMap,
					});
				}
			} catch (error) {
				// Only track error if component is still mounted
				if (isMounted && userInfo?.id) {
					const errorData = {
						count: unseenNotificationIds.length,
						error_details: (error as Error).message,
					};

					trackErrorEvent("notifications_mark_seen_failed", error, {
						user_id: userInfo.id,
						...errorData,
					});
				}
			}
		};

		if (allNotifications && allNotifications.length > 0 && userInfo?.id) {
			markAllAsSeen();
		}

		return () => {
			isMounted = false;
		};
	}, [allNotifications, markNotificationsSeen, userInfo?.id]); // Only depend on userInfo.id

	const handleEmptyStateClick = () => {
		trackNotificationEvent("empty_notifications_view_clicked", {});
	};

	// Clear badge when page is loaded for mobile
	useEffect(() => {
		if (Capacitor.isNativePlatform()) {
			pushNotificationService.clearBadge();
		}
	}, []);

	return (
		<Box
			data-testid="notifications-root"
			sx={{
				height: "100%",
				width: "100%",
				bgcolor: "#F8F9FC",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				gap: 1,
				overflow: "auto",
				scrollBehavior: "smooth",
				overscrollBehavior: "contain",
				pb: 10,
				maxWidth: "lg",
				mx: "auto",
				p: 2,
			}}
		>
			{isLoading ? (
				<>
					<NotificationSkeleton />
					<NotificationSkeleton />
					<NotificationSkeleton />
					<NotificationSkeleton />
					<NotificationSkeleton />
				</>
			) : notifications.length === 0 ? (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						height: "100%",
						gap: 2,
					}}
					onClick={handleEmptyStateClick}
				>
					<NotificationsNoneIcon
						sx={{
							fontSize: 64,
							color: "#6B7280",
							opacity: 0.5,
						}}
					/>
					<Typography variant="body1" color="textSecondary" sx={{ opacity: 0.7 }}>
						{t("you.are.all.set")}
					</Typography>
					<Typography variant="body2" color="textSecondary" sx={{ opacity: 0.5 }}>
						{t("nothing.new.here")}
					</Typography>
				</Box>
			) : (
				notifications.map((notification) => (
					<Box key={notification.id} data-testid={`notification-item-${notification.id}`} sx={{ width: "100%" }}>
						<NotificationItem notification={notification} markNotificationClicked={markNotificationClicked} />
					</Box>
				))
			)}
		</Box>
	);
};
